// Centralized, reusable platform × asset-type format spec shared by the
// Clips and Images & Posts workspaces (Level 1/2 — UI-only; no backend
// duration/format enforcement exists yet, so this never claims automatic
// compliance, only suggests a sensible starting aspect ratio reusing the
// 4 ratios the backend's MasterComposition/EditorClip models already
// validate: 16:9/9:16/1:1/4:5).

export const PLATFORMS = [
  { key: 'youtube', label: 'YouTube' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'tiktok', label: 'TikTok' },
];

// Video variant types (Clips). Unchanged — reused as-is by ClipVariantGenerator.
export const ASSET_TYPES = [
  { key: 'long_form', label: 'Long-form Video', aspect: '16:9' },
  { key: 'short_form', label: 'Short-form Video', aspect: '9:16' },
  { key: 'square_post', label: 'Square Video Post', aspect: '1:1' },
  { key: 'portrait_post', label: 'Portrait Video Post', aspect: '4:5' },
];

// Image/post variant types (Images & Posts).
export const IMAGE_ASSET_TYPES = [
  { key: 'image_post', label: 'Image Post', aspect: '1:1' },
  { key: 'graphic', label: 'Graphic', aspect: '1:1' },
  { key: 'thumbnail', label: 'Thumbnail', aspect: '16:9' },
  { key: 'carousel', label: 'Carousel', aspect: '1:1' },
  { key: 'text_post', label: 'Text-Based Visual Post', aspect: '1:1' },
];

// Story is ONE shared asset-type concept across both Editors (never a
// separate Production Tool) — Images & Posts owns the `image` kinds
// (static/multi-image), Clips owns the `video` kinds (single video or
// mixed video+image timeline-based). Same terminology/aspect everywhere.
export const STORY_ASSET_TYPES = [
  { key: 'image_story', label: 'Image Story', aspect: '9:16', mediaKind: 'image' },
  { key: 'multi_image_story', label: 'Multi-Image Story', aspect: '9:16', mediaKind: 'image' },
  { key: 'video_story', label: 'Video Story', aspect: '9:16', mediaKind: 'video' },
  { key: 'mixed_story', label: 'Mixed-Media Story', aspect: '9:16', mediaKind: 'video' },
];

const ALL_ASSET_TYPES = [...ASSET_TYPES, ...IMAGE_ASSET_TYPES, ...STORY_ASSET_TYPES];

// Which asset types each platform actually supports — do not assume every
// platform supports every format. Kept in one place so every picker reuses
// the same truth.
export const PLATFORM_SUPPORT = {
  youtube: ['long_form', 'short_form', 'thumbnail', 'video_story'],
  facebook: ['long_form', 'short_form', 'square_post', 'portrait_post', 'image_post', 'graphic', 'thumbnail', 'carousel', 'text_post', 'image_story', 'multi_image_story', 'video_story', 'mixed_story'],
  instagram: ['short_form', 'square_post', 'portrait_post', 'image_post', 'graphic', 'carousel', 'image_story', 'multi_image_story', 'video_story', 'mixed_story'],
  tiktok: ['short_form', 'video_story', 'mixed_story'],
};

// Rough, resolution-independent reference canvas size per aspect ratio —
// just a sane default for a new design's canvas, never a guaranteed
// render resolution (there is no server-side image renderer here).
const CANVAS_DIMS_BY_ASPECT = {
  '1:1': { w: 1080, h: 1080 },
  '4:5': { w: 1080, h: 1350 },
  '9:16': { w: 1080, h: 1920 },
  '16:9': { w: 1920, h: 1080 },
};
export function canvasDimsForAspect(ratio) {
  return CANVAS_DIMS_BY_ASPECT[ratio] || CANVAS_DIMS_BY_ASPECT['1:1'];
}

export function assetTypeLabel(key) {
  return ALL_ASSET_TYPES.find((a) => a.key === key)?.label || key;
}
export function aspectForAssetType(key) {
  return ALL_ASSET_TYPES.find((a) => a.key === key)?.aspect || '16:9';
}
export function platformLabel(key) {
  return PLATFORMS.find((p) => p.key === key)?.label || key;
}
export function supportsAssetType(platformKey, assetTypeKey) {
  return (PLATFORM_SUPPORT[platformKey] || []).includes(assetTypeKey);
}

